import { AnimatePresence, motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'

interface AiProcessingOverlayProps {
  /** Usually a mutation's `isPending`. */
  open: boolean
  title?: string
  subtitle?: string
  /** Step labels shown left to right; the last one holds until the op ends. */
  steps?: string[]
}

/** How long each step stays active before the next lights up (ms). Sums to
 *  ~15s to reach the final step, which then holds (pulsing) until the
 *  operation resolves - or closes early if it finishes first. */
const STEP_DURATIONS = [3000, 3500, 4000, 4500]

const SPRING_EASE = [0.16, 1, 0.3, 1] as const

function defaultSteps(): string[] {
  return [
    m.aiLoaderStepAnalysing(),
    m.aiLoaderStepGathering(),
    m.aiLoaderStepThinking(),
    m.aiLoaderStepBuilding(),
    m.aiLoaderStepFinishing(),
  ]
}

export function AiProcessingOverlay({ open, title, subtitle, steps }: AiProcessingOverlayProps) {
  const stepLabels = steps ?? defaultSteps()
  const [active, setActive] = useState(0)

  useEffect(() => {
    // While closing, leave `active` on its last step so the status line holds
    // through the fade-out instead of snapping back to step 0.
    if (!open) return
    setActive(0)
    const lastIndex = stepLabels.length - 1
    let elapsed = 0
    const timers = STEP_DURATIONS.slice(0, lastIndex).map((duration, index) => {
      elapsed += duration
      return window.setTimeout(() => setActive(index + 1), elapsed)
    })
    return () => timers.forEach(clearTimeout)
  }, [open, stepLabels.length])

  // Portalled to the body because the dashboard canvas sets `isolation: isolate`,
  // which traps any z-index inside it - a dialog, which Radix portals to the body,
  // would otherwise always paint over this overlay no matter how high it sits.
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-md"
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.32, ease: SPRING_EASE }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border bg-card p-8"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute -top-16 -right-10 h-40 w-40 rounded-full bg-brand/20 blur-3xl"
            />

            <div className="relative flex flex-col items-center text-center">
              <PulsifyLoader className="h-20 w-auto" />

              <h2 className="mt-5 font-semibold text-xl text-foreground tracking-[-0.02em]">
                {title ?? m.aiLoaderTitle()}
              </h2>
              <p className="mt-1.5 text-muted-foreground text-sm">
                {subtitle ?? m.aiLoaderSubtitle()}
              </p>

              <div className="mt-7 flex w-full items-center">
                {stepLabels.map((label, index) => {
                  const state = index < active ? 'done' : index === active ? 'active' : 'upcoming'
                  return (
                    <Fragment key={label}>
                      {index > 0 && (
                        <div className="h-0.5 flex-1 overflow-hidden rounded-full bg-border">
                          <div
                            className={cn(
                              'h-full rounded-full bg-brand transition-all duration-500 ease-out',
                              index <= active ? 'w-full' : 'w-0',
                            )}
                          />
                        </div>
                      )}
                      <span
                        className={cn(
                          'relative flex size-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-300',
                          state === 'upcoming' && 'border-border bg-card',
                          state !== 'upcoming' &&
                            'border-transparent bg-brand text-brand-foreground',
                        )}
                      >
                        {state === 'active' && (
                          <span className="absolute inset-0 motion-safe:animate-ping rounded-full bg-brand/40" />
                        )}
                        {state === 'done' ? (
                          <Check className="relative size-3.5" />
                        ) : (
                          <span
                            className={cn(
                              'relative size-2 rounded-full',
                              state === 'active' ? 'bg-brand-foreground' : 'bg-border',
                            )}
                          />
                        )}
                      </span>
                    </Fragment>
                  )
                })}
              </div>

              <div className="mt-5 h-6 w-full overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={active}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: SPRING_EASE }}
                    className="font-medium text-foreground text-sm"
                  >
                    {stepLabels[active]}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
